import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { api } from "@/api/client";
import { useManageBusiness } from "@/pages/manage/ManageShell";
import { useAuth } from "@/context/AuthContext";
import { getTheme } from "@/lib/theme";

const FIELDS = [
  { key: "bakery_name", label: "Business name" },
  { key: "tagline", label: "Tagline" },
  { key: "about", label: "About", textarea: true },
  { key: "zelle_handle", label: "Zelle handle" },
  { key: "zelle_name", label: "Zelle name" },
  { key: "cashapp_handle", label: "CashApp handle" },
  { key: "instagram_handle", label: "Instagram handle" },
  { key: "delivery_address", label: "Delivery base address" },
];

export default function Settings() {
  const { businessId, setBusinessId } = useManageBusiness();
  const { user, refresh } = useAuth();
  const theme = getTheme(businessId);
  const [info, setInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [savingSide, setSavingSide] = useState(false);
  const [paused, setPaused] = useState(false);
  const [savingPause, setSavingPause] = useState(false);

  useEffect(() => {
    api.listBusinesses().then((all) => {
      const b = (all || []).find((x) => x.id === businessId);
      setPaused(!!b?.ordering_paused);
    }).catch(() => {});
  }, [businessId]);

  const togglePause = async () => {
    const next = !paused;
    setSavingPause(true);
    try {
      const b = await api.pauseOrdering(businessId, next);
      setPaused(!!b.ordering_paused);
      toast.success(next ? "Ordering paused — customers can browse but not check out" : "Ordering resumed");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingPause(false);
    }
  };

  const saveMainSide = async (side) => {
    if (side === user?.business_id) return;
    setSavingSide(true);
    try {
      await api.updateHomeBusiness(side);
      await refresh();
      setBusinessId(side);
      toast.success(`Main side set to ${side === "rubys" ? "Catering" : "Baking"}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingSide(false);
    }
  };

  useEffect(() => {
    api.getPaymentInfo(businessId).then(setInfo).catch(() => toast.error("Could not load settings"));
  }, [businessId]);

  useEffect(() => {
    api.getNotifyPrefs().then(setPrefs).catch(() => {});
  }, []);

  const set = (k, v) => setInfo((p) => ({ ...p, [k]: v }));
  const setPref = (k, v) => setPrefs((p) => ({ ...p, [k]: v }));

  const savePrefs = async () => {
    setSavingPrefs(true);
    try {
      const saved = await api.updateNotifyPrefs({
        notify_email: (prefs.notify_email || "").trim(),
        notify_phone: (prefs.notify_phone || "").trim(),
        notify_new_accounts: !!prefs.notify_new_accounts,
        notify_new_orders: !!prefs.notify_new_orders,
      });
      setPrefs(saved);
      toast.success("Alert preferences saved");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingPrefs(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      FIELDS.forEach((f) => (body[f.key] = info[f.key] ?? ""));
      body.deposit_percent = parseFloat(info.deposit_percent) || 5;
      body.delivery_near_miles = parseFloat(info.delivery_near_miles) || 10;
      body.delivery_far_miles = parseFloat(info.delivery_far_miles) || 25;
      await api.updatePaymentInfo(body, businessId);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!info) return <div className="h-40 rounded-2xl animate-pulse" style={{ background: "var(--surface-2)" }} />;

  const inputStyle = { background: "var(--surface)", border: "1px solid var(--border)" };

  return (
    <div className="max-w-2xl">
      <h1 className="font-display text-3xl" style={{ color: "var(--ink)" }}>Settings</h1>
      <p className="text-sm" style={{ color: "var(--muted)" }}>{theme.name}</p>

      <div className="mt-6 rounded-2xl p-6 space-y-4" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
        <div className="rounded-xl p-4" style={{ background: "var(--accent-soft)" }}>
          <label className="text-sm font-semibold block mb-1.5" style={{ color: "var(--ink)" }}>Default deposit percent</label>
          <div className="flex items-center gap-2">
            <input type="number" value={info.deposit_percent ?? 5} onChange={(e) => set("deposit_percent", e.target.value)} data-testid="deposit-percent-input" className="w-28 rounded-xl px-3 py-2 outline-none" style={inputStyle} />
            <span className="text-sm" style={{ color: "var(--muted)" }}>% charged upfront to reserve an order</span>
          </div>
        </div>

        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-sm font-semibold block mb-1.5" style={{ color: "var(--ink)" }}>{f.label}</label>
            {f.textarea ? (
              <textarea value={info[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} rows={3} data-testid={`setting-${f.key}`} className="w-full rounded-xl px-3 py-2 outline-none resize-none" style={inputStyle} />
            ) : (
              <input value={info[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`setting-${f.key}`} className="w-full rounded-xl px-3 py-2 outline-none" style={inputStyle} />
            )}
          </div>
        ))}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-semibold block mb-1.5" style={{ color: "var(--ink)" }}>Near delivery (mi)</label>
            <input type="number" value={info.delivery_near_miles ?? 10} onChange={(e) => set("delivery_near_miles", e.target.value)} className="w-full rounded-xl px-3 py-2 outline-none" style={inputStyle} />
          </div>
          <div>
            <label className="text-sm font-semibold block mb-1.5" style={{ color: "var(--ink)" }}>Far delivery (mi)</label>
            <input type="number" value={info.delivery_far_miles ?? 25} onChange={(e) => set("delivery_far_miles", e.target.value)} className="w-full rounded-xl px-3 py-2 outline-none" style={inputStyle} />
          </div>
        </div>

        <button onClick={save} disabled={saving} data-testid="save-settings-btn" className="btn-brand rounded-xl px-6 py-3 font-semibold inline-flex items-center gap-2 disabled:opacity-60">
          <Save size={17} /> {saving ? "Saving…" : "Save settings"}
        </button>
      </div>

      <div className="mt-6 rounded-2xl p-6" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="font-display text-xl" style={{ color: "var(--ink)" }}>Pause ordering</h2>
            <p className="text-sm mt-0.5" style={{ color: "var(--muted)" }}>
              Temporarily stop taking new orders for {theme.name}. Customers can still browse the menu, but checkout is disabled.
            </p>
          </div>
          <button
            onClick={togglePause}
            disabled={savingPause}
            data-testid="pause-ordering-toggle"
            aria-pressed={paused}
            className="relative shrink-0 h-7 w-12 rounded-full transition disabled:opacity-60"
            style={{ background: paused ? "#b91c1c" : "var(--border)" }}
          >
            <span className="absolute top-0.5 h-6 w-6 rounded-full bg-white transition-all" style={{ left: paused ? "22px" : "2px" }} />
          </button>
        </div>
        {paused && (
          <div className="mt-3 rounded-xl px-4 py-2.5 text-sm font-semibold" style={{ background: "#fdecec", color: "#b91c1c" }} data-testid="paused-banner-settings">
            Ordering is paused. Customers can&apos;t check out until you resume.
          </div>
        )}
      </div>

      <div className="mt-6 rounded-2xl p-6 space-y-4" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
        <div>
          <h2 className="font-display text-xl" style={{ color: "var(--ink)" }}>Your main side</h2>
          <p className="text-sm" style={{ color: "var(--muted)" }}>
            The dashboard you open on by default. You can still switch sides anytime from the top bar.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 p-1 rounded-xl max-w-xs" style={{ background: "var(--surface-2)" }}>
          {[
            { id: "baking_jays", label: "Baking" },
            { id: "rubys", label: "Catering" },
          ].map((b) => (
            <button
              key={b.id}
              onClick={() => saveMainSide(b.id)}
              disabled={savingSide}
              data-testid={`main-side-${b.id}`}
              className="py-2 rounded-lg text-sm font-semibold transition disabled:opacity-60"
              style={user?.business_id === b.id ? { background: "var(--surface)", color: "var(--ink)", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" } : { color: "var(--muted)" }}
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 rounded-2xl p-6 space-y-4" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
        <div>
          <h2 className="font-display text-xl" style={{ color: "var(--ink)" }}>Order & account alerts</h2>
          <p className="text-sm" style={{ color: "var(--muted)" }}>
            Get notified by email and text when a new account signs up or a new order comes in. Applies to your account across both businesses.
          </p>
        </div>

        {prefs ? (
          <>
            <div>
              <label className="text-sm font-semibold block mb-1.5" style={{ color: "var(--ink)" }}>Alert email</label>
              <input value={prefs.notify_email ?? ""} onChange={(e) => setPref("notify_email", e.target.value)} placeholder="you@example.com" data-testid="notify-email-input" className="w-full rounded-xl px-3 py-2 outline-none" style={inputStyle} />
            </div>
            <div>
              <label className="text-sm font-semibold block mb-1.5" style={{ color: "var(--ink)" }}>Alert phone (SMS)</label>
              <input value={prefs.notify_phone ?? ""} onChange={(e) => setPref("notify_phone", e.target.value)} placeholder="+14155552671" data-testid="notify-phone-input" className="w-full rounded-xl px-3 py-2 outline-none" style={inputStyle} />
              <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>Use international format with country code. Leave blank for no texts.</p>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={!!prefs.notify_new_orders} onChange={(e) => setPref("notify_new_orders", e.target.checked)} data-testid="notify-orders-toggle" className="h-4 w-4" />
              <span className="text-sm font-semibold" style={{ color: "var(--ink)" }}>Notify me about new orders</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={!!prefs.notify_new_accounts} onChange={(e) => setPref("notify_new_accounts", e.target.checked)} data-testid="notify-accounts-toggle" className="h-4 w-4" />
              <span className="text-sm font-semibold" style={{ color: "var(--ink)" }}>Notify me about new accounts</span>
            </label>
            <button onClick={savePrefs} disabled={savingPrefs} data-testid="save-notify-prefs-btn" className="btn-brand rounded-xl px-6 py-3 font-semibold inline-flex items-center gap-2 disabled:opacity-60">
              <Save size={17} /> {savingPrefs ? "Saving…" : "Save alert preferences"}
            </button>
          </>
        ) : (
          <div className="h-24 rounded-xl animate-pulse" style={{ background: "var(--surface-2)" }} />
        )}
      </div>
    </div>
  );
}
